/**
 * Deliver To dropdown — switches between AU / UK / EU / USA storefronts.
 * Builds a CartJS ?PerC= handoff URL from the live cart before navigating.
 * Supports hover open/close in the announcement bar.
 */

import { Component } from '@theme/component';

const ANNOUNCEMENT_MENU_OPEN_EVENT = 'announcement-menu:open';

class DeliverToDropdown extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    if (this.hasAttribute('data-announcement-menu')) {
      this.addEventListener('mouseenter', this.showPanel);
      this.addEventListener('mouseleave', this.hidePanel);
      this.addEventListener('focusin', this.showPanel);
      this.addEventListener('focusout', this.#handleFocusOut);
      document.addEventListener(ANNOUNCEMENT_MENU_OPEN_EVENT, this.#onOpenElsewhere);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('click', this.#handleClickOutside);
    document.removeEventListener('keyup', this.#handleKeyUp);
    document.removeEventListener(ANNOUNCEMENT_MENU_OPEN_EVENT, this.#onOpenElsewhere);
  }

  get isHidden() {
    return !this.refs.panel || this.refs.panel.hasAttribute('hidden');
  }

  #onOpenElsewhere = (event) => {
    if (event.detail?.source === this) return;
    this.hidePanel();
  };

  toggleSelector() {
    if (this.isHidden) {
      this.showPanel();
      return;
    }
    if (
      this.hasAttribute('data-announcement-menu')
      && window.matchMedia('(hover: hover) and (pointer: fine)').matches
    ) {
      return;
    }
    this.hidePanel();
  }

  showPanel = () => {
    if (!this.refs.panel || !this.isHidden) return;
    if (this.hasAttribute('data-announcement-menu')) {
      document.dispatchEvent(
        new CustomEvent(ANNOUNCEMENT_MENU_OPEN_EVENT, {
          bubbles: true,
          detail: { source: this },
        })
      );
    }
    document.addEventListener('keyup', this.#handleKeyUp);
    this.refs.panel.removeAttribute('hidden');
    this.refs.button?.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      document.addEventListener('click', this.#handleClickOutside);
    });
  };

  hidePanel = () => {
    if (!this.refs.panel || this.isHidden) return;
    document.removeEventListener('keyup', this.#handleKeyUp);
    document.removeEventListener('click', this.#handleClickOutside);
    this.refs.button?.setAttribute('aria-expanded', 'false');
    this.refs.panel.setAttribute('hidden', '');
  };

  #handleKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    this.hidePanel();
    event.stopPropagation();
    this.refs.button?.focus();
  };

  #handleClickOutside = (event) => {
    const target = event.target;
    if (!(target instanceof Node)) return;
    if (this.contains(target)) return;
    this.hidePanel();
  };

  #handleFocusOut = (event) => {
    const related = event.relatedTarget;
    if (related instanceof Node && this.contains(related)) return;
    this.hidePanel();
  };

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('[data-deliver-to-destination]');
    if (!(button instanceof HTMLElement)) return;
    event.preventDefault();
    void this.#switchTo(button);
  };

  /**
   * @param {HTMLElement} button
   */
  async #switchTo(button) {
    const baseUrl = (button.getAttribute('data-deliver-to-url') || '').trim();
    if (!baseUrl) return;

    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');

    try {
      let destination = baseUrl;
      const cartJs = /** @type {any} */ (window).CartJS;
      if (cartJs && typeof cartJs.buildShareUrlFromCart === 'function') {
        destination = await cartJs.buildShareUrlFromCart(baseUrl);
      }
      window.location.assign(destination);
    } catch (error) {
      console.error('[deliver-to] handoff failed, navigating without cart', error);
      window.location.assign(baseUrl);
    }
  }
}

if (!customElements.get('deliver-to-dropdown')) {
  customElements.define('deliver-to-dropdown', DeliverToDropdown);
}
